import mitt, { type Emitter } from "mitt"
import type { FileRelation } from "@/models/file/file-relation.model.ts"
import type { Location } from "@/models/location.model"

export type LocationMentionFocusPayload = {
  id: string
  latitude: number
  longitude: number
  name?: string | null
  address?: string | null
}

type Events = {
  "delete-file-relation": { fileRelation: FileRelation }
  "location-mention-focus": LocationMentionFocusPayload
  "location-mention-focus-clear": { id?: string }
  "location-map-modal-open": { location: Location }
  "instruction-text-changed": undefined
}

export class Bus {
  // Singleton instance
  private static _instance: Bus | null = null

  // Underlying mitt emitter
  private emitter: Emitter<Events>

  // Track attached listener IDs per event
  private listenerIds: Map<keyof Events, Set<string>>

  // Map handlers back to their ID, for cleanup
  private handlerIdMap: Map<(...args: unknown[]) => void, string>

  // Private constructor
  private constructor() {
    this.emitter = mitt<Events>()
    this.listenerIds = new Map()
    this.handlerIdMap = new Map()
  }

  /**
   * Acquire the singleton bus
   */
  public static getInstance(): Bus {
    if (!this._instance) {
      this._instance = new Bus()
    }
    return this._instance
  }

  /**
   * Subscribe to an event, optionally dedup by a unique ID
   * @param event    Event name
   * @param handler  Callback function
   * @param id       Optional unique identifier to prevent duplicate subscriptions
   */
  public static on<K extends keyof Events>(
    event: K,
    handler: (payload: Events[K]) => void,
    id?: string
  ): void {
    const bus = Bus.getInstance()

    if (id) {
      const ids = bus.listenerIds.get(event) ?? new Set<string>()
      if (ids.has(id)) {
        return // already subscribed with this ID
      }
      ids.add(id)
      bus.listenerIds.set(event, ids)
      bus.handlerIdMap.set(handler as (...args: unknown[]) => void, id)
    }

    bus.emitter.on(event, handler)
  }

  /**
   * Unsubscribe from an event and remove its ID
   * @param event    Event name
   * @param handler  The same handler passed to `on`
   */
  public static off<K extends keyof Events>(event: K, handler: (payload: Events[K]) => void): void {
    const bus = Bus.getInstance()
    bus.emitter.off(event, handler)

    const id = bus.handlerIdMap.get(handler as (...args: unknown[]) => void)
    if (id) {
      const ids = bus.listenerIds.get(event)
      ids?.delete(id)
      bus.handlerIdMap.delete(handler as (...args: unknown[]) => void)
    }
  }

  /**
   * Emit an event
   */
  public static emit<K extends keyof Events>(event: K, payload: Events[K]): void {
    Bus.getInstance().emitter.emit(event, payload)
  }
}
